import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { TOOLTIP_GAP } from './tooltipConsts';

type TooltipProps = {
  children: React.ReactNode;
  anchorRef: React.RefObject<HTMLElement>;
  open: boolean;
  side?: 'right' | 'left' | 'top' | 'bottom';
  /** pixels between anchor and tooltip */
  offset?: number;
  /** ms */
  showDelay?: number;
  /** ms */
  hideDelay?: number;
  customClassName?: string;
};

export function Tooltip({
  children,
  anchorRef,
  open,
  side = 'right',
  offset = TOOLTIP_GAP,
  showDelay = 60,
  hideDelay = 60,
  customClassName,
}: TooltipProps) {
  const el = useRef<HTMLDivElement | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const [visible, setVisible] = useState<boolean>(open);
  const [showing, setShowing] = useState<boolean>(false);
  const [actualSide, setActualSide] = useState<typeof side>(side);

  // create portal node
  useEffect(() => {
    if (!el.current) el.current = document.createElement('div');
    const node = el.current;
    document.body.appendChild(node);
    return () => {
      if (node.parentNode) node.parentNode.removeChild(node);
    };
  }, []);

  // handle open/close with delays for nicer UX
  useEffect(() => {
    let showTimer: number | undefined;
    let hideTimer: number | undefined;

    if (open) {
      clearTimeout(hideTimer);
      showTimer = window.setTimeout(() => {
        setVisible(true);
        // measure and position on next frame after the element is mounted
        requestAnimationFrame(() => {
          positionTooltip();
          // small stagger so transform animation looks smooth
          setTimeout(() => setShowing(true), 10);
        });
      }, showDelay);
    } else {
      // start hide animation
      setShowing(false);
      hideTimer = window.setTimeout(() => {
        setVisible(false);
      }, hideDelay);
    }

    return () => {
      if (showTimer) clearTimeout(showTimer);
      if (hideTimer) clearTimeout(hideTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, anchorRef, side, showDelay, hideDelay, offset]);

  // Reposition on window resize / scroll while visible
  useEffect(() => {
    if (!visible) return;
    const handler = () => positionTooltip();
    window.addEventListener('resize', handler);
    window.addEventListener('scroll', handler, true);
    return () => {
      window.removeEventListener('resize', handler);
      window.removeEventListener('scroll', handler, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, actualSide]);

  function positionTooltip() {
    if (!anchorRef.current || !el.current) return;

    const rect = anchorRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // measure tooltip size if rendered
    const tip = tooltipRef.current;
    const tipW = tip ? tip.offsetWidth : 140; // fallback estimate
    const tipH = tip ? tip.offsetHeight : 28;

    // prefer the requested side, but flip if we'd overflow
    let chosenSide = side;

    if (side === 'right') {
      const leftCandidate = rect.right + offset;
      if (leftCandidate + tipW > viewportWidth - TOOLTIP_GAP) chosenSide = 'left';
    } else if (side === 'left') {
      const leftCandidate = rect.left - offset - tipW;
      if (leftCandidate < TOOLTIP_GAP) chosenSide = 'right';
    } else if (side === 'top') {
      const topCandidate = rect.top - offset - tipH;
      if (topCandidate < TOOLTIP_GAP) chosenSide = 'bottom';
    } else if (side === 'bottom') {
      const topCandidate = rect.bottom + offset + tipH;
      if (topCandidate > viewportHeight - TOOLTIP_GAP) chosenSide = 'top';
    }

    let top = rect.top + rect.height / 2 - tipH / 2;
    let left = rect.right + offset;

    if (chosenSide === 'right') {
      // Always outside the button border
      left = rect.right + offset;
      top = rect.top + rect.height / 2 - tipH / 2;
    } else if (chosenSide === 'left') {
      left = rect.left - offset - tipW;
      top = rect.top + rect.height / 2 - tipH / 2;
    } else if (chosenSide === 'top') {
      top = rect.top - offset - tipH;
      left = rect.left + rect.width / 2 - tipW / 2;
    } else if (chosenSide === 'bottom') {
      top = rect.bottom + offset;
      left = rect.left + rect.width / 2 - tipW / 2;
    }

    // clamp to viewport with small padding
    const pad = TOOLTIP_GAP;
    left = Math.min(Math.max(left, pad), viewportWidth - tipW - pad);
    top = Math.min(Math.max(top, pad), viewportHeight - tipH - pad);

    setActualSide(chosenSide);
    setPos({ top: Math.round(top), left: Math.round(left) });
  }

  if (!el.current) return null;
  if (!visible) return null;

  // compute transform based on side and animation state
  let transform = '';
  if (actualSide === 'right') {
    transform = `translateX(${showing ? 0 : 8}px)`;
  } else if (actualSide === 'left') {
    transform = `translateX(${showing ? 0 : -8}px)`;
  } else if (actualSide === 'top') {
    transform = `translateY(${showing ? 0 : -8}px)`;
  } else {
    // bottom
    transform = `translateY(${showing ? 0 : 8}px)`;
  }

  return createPortal(
    <div
      style={{ position: 'fixed', top: pos.top, left: pos.left, transform, zIndex: 2147483647 }}
      className={`pointer-events-none transition-opacity duration-150 ease-out ${showing ? 'opacity-100' : 'opacity-0'}`}
      aria-hidden={!showing}
    >
      <div
        ref={tooltipRef}
        className={
          customClassName
            ? `whitespace-nowrap rounded-lg text-xs px-2 py-1 shadow-lg border ${customClassName}`
            : "whitespace-nowrap rounded-lg bg-blue-500 text-white text-xs px-2 py-1 shadow-lg border border-white/10 dark:bg-blue-600/95 dark:text-white dark:border-blue-700"
        }
      >
        {children}
      </div>
    </div>,
    el.current
  );
}

export default Tooltip;
